import { useState, useCallback, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { 
  SharedWrapperPatternService, 
  WrapperPatternContext, 
  StoredWrapperPattern, 
  HtmlWrapperInferenceResult 
} from '../services/sharedWrapperPatternService';

export interface UseWrapperPatternsConfig {
  context: WrapperPatternContext;
  urls: string[];
  autoLoad?: boolean;
}

export interface UseWrapperPatternsReturn {
  // State
  patterns: StoredWrapperPattern[];
  savedPatterns: StoredWrapperPattern[];
  selectedPatterns: Set<number>;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  
  // Actions
  findPatterns: () => Promise<HtmlWrapperInferenceResult | undefined>;
  saveSelectedPatterns: () => Promise<void>;
  loadSavedPatterns: () => Promise<void>;
  togglePatternSelection: (index: number) => void;
  selectAllPatterns: () => void;
  clearSelection: () => void;
  removePattern: (patternToRemove: string) => Promise<void>; // <-- add here
}

export const useWrapperPatterns = ({
  context,
  urls,
  autoLoad = true
}: UseWrapperPatternsConfig): UseWrapperPatternsReturn & { setPatterns: React.Dispatch<React.SetStateAction<StoredWrapperPattern[]>> } => {
  // State
  const [patterns, setPatterns] = useState<StoredWrapperPattern[]>([]);
  const [savedPatterns, setSavedPatterns] = useState<StoredWrapperPattern[]>([]);
  const [selectedPatterns, setSelectedPatterns] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load saved patterns on mount if autoLoad is true
  useEffect(() => {
    if (autoLoad) {
      loadSavedPatterns();
    }
  }, [context.locationId, autoLoad]);

  // Load saved patterns
  const loadSavedPatterns = useCallback(async () => {
    if (!context.locationId) {
      setError('No locationId provided for pattern operations');
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const loadedPatterns = await SharedWrapperPatternService.loadPatterns(context);
      setSavedPatterns(loadedPatterns);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load saved patterns');
      console.error('[useWrapperPatterns] Error loading saved patterns:', err);
    } finally {
      setIsLoading(false);
    }
  }, [context]);

  // Find patterns from URLs
  const findPatterns = useCallback(async () => {
    if (!urls.length) {
      setError('No URLs provided for pattern inference');
      return;
    }

    try {
      setIsFetching(true);
      setError(null);
      
      const result = await SharedWrapperPatternService.inferPatterns(urls, context);
      
      if (result.success) {
        const newPatterns = result.patterns.map(p => ({
          ...p,
          createdAt: Timestamp.now()
        }));
        setPatterns(newPatterns);
        setSelectedPatterns(new Set()); // Clear selection when new patterns arrive
        return result; // Return the full result
      } else {
        setError(result.error || 'Failed to infer patterns');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to infer patterns');
      console.error('[useWrapperPatterns] Error inferring patterns:', err);
    } finally {
      setIsFetching(false);
    }
  }, [urls, context]);

  // Save selected patterns
  const saveSelectedPatterns = useCallback(async () => {
    if (!context.locationId) {
      setError('No locationId provided for pattern operations');
      return;
    }
    try {
      console.log(`[useWrapperPatterns] Starting saveSelectedPatterns with context:`, {
        type: context.type,
        locationId: context.locationId,
        selectedCount: selectedPatterns.size,
        totalPatterns: patterns.length
      });

      setIsLoading(true);
      setError(null);

      // Get selected patterns
      const selectedPatternsArray = Array.from(selectedPatterns)
        .map(index => patterns[index])
        .filter((p): p is StoredWrapperPattern => p !== undefined);

      console.log(`[useWrapperPatterns] Selected patterns to save:`, selectedPatternsArray);

      if (!selectedPatternsArray.length) {
        const error = 'No patterns selected';
        console.error(`[useWrapperPatterns] ${error}`);
        setError(error);
        return;
      }

      // Validate patterns before saving
      const validation = SharedWrapperPatternService.validatePatterns(selectedPatternsArray);
      console.log(`[useWrapperPatterns] Pattern validation result:`, validation);
      
      if (!validation.isValid) {
        const error = `Invalid patterns: ${validation.errors.join(', ')}`;
        console.error(`[useWrapperPatterns] ${error}`);
        setError(error);
        return;
      }

      // Deduplicate patterns
      const uniquePatterns = SharedWrapperPatternService.deduplicatePatterns(selectedPatternsArray);
      console.log(`[useWrapperPatterns] Unique patterns after deduplication:`, uniquePatterns);

      // Strip urlsMatched before saving
      const patternsToSave = uniquePatterns.map(({ pattern, description, createdAt, confidence, avgContentScore }) => ({
        pattern,
        description,
        createdAt,
        confidence,
        avgContentScore
      }));
      console.log(`[useWrapperPatterns] Patterns to save (urlsMatched stripped):`, patternsToSave);

      // Save patterns
      console.log(`[useWrapperPatterns] Calling SharedWrapperPatternService.savePatterns...`);
      await SharedWrapperPatternService.savePatterns(context, patternsToSave);
      
      // Reload saved patterns to reflect changes
      console.log(`[useWrapperPatterns] Reloading saved patterns...`);
      await loadSavedPatterns();
      
      // Clear selection after successful save
      console.log(`[useWrapperPatterns] Save successful, clearing selection`);
      setSelectedPatterns(new Set());
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to save patterns';
      console.error('[useWrapperPatterns] Error saving patterns:', err);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [patterns, selectedPatterns, context, loadSavedPatterns]);

  // Pattern selection handlers
  const togglePatternSelection = useCallback((index: number) => {
    setSelectedPatterns(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const selectAllPatterns = useCallback(() => {
    setSelectedPatterns(new Set(patterns.map((_, index) => index)));
  }, [patterns]);

  const clearSelection = useCallback(() => {
    setSelectedPatterns(new Set());
  }, []);

  // Remove a pattern
  const removePattern = useCallback(async (patternToRemove: string) => {
    if (!context.locationId) {
      setError('No locationId provided for pattern operations');
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      await SharedWrapperPatternService.removePattern(context, patternToRemove);
      await loadSavedPatterns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove pattern');
      console.error('[useWrapperPatterns] Error removing pattern:', err);
    } finally {
      setIsLoading(false);
    }
  }, [context, loadSavedPatterns]);

  return {
    // State
    patterns,
    savedPatterns,
    selectedPatterns,
    isLoading,
    isFetching,
    error,
    
    // Actions
    findPatterns,
    saveSelectedPatterns,
    loadSavedPatterns,
    togglePatternSelection,
    selectAllPatterns,
    clearSelection,
    setPatterns,
    removePattern // <-- expose here
  };
}; 